import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { PageHeader, StatusBadge, EmptyState } from '../../components/ui';
import { jobsApi } from '../../api/jobs';
import { formatCurrency, formatDate, formatDateTime } from '../../utils';
import type { ServiceRequest } from '../../types';

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || '—'}
    </Typography>
  </Box>
);

const RequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    jobsApi
      .getJobDetails(id)
      .then(setItem)
      .catch(() => {
        toast.error('Request not found');
        navigate('/requests');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !item) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={item.id}
        subtitle={`${item.category} · ${item.customerName}`}
        breadcrumbs={[
          { label: 'Requests', to: '/requests' },
          { label: item.id },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <StatusBadge status={item.status} />
            <StatusBadge status={item.paymentStatus} />
          </Box>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Customer Info
              </Typography>
              <InfoRow
                label="Name"
                value={
                  <Link component={RouterLink} to={`/users/${item.customerId}`} underline="hover">
                    {item.customerName}
                  </Link>
                }
              />
              <InfoRow label="Customer ID" value={item.customerId} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Assigned Provider
              </Typography>
              {item.providerId && item.providerName ? (
                <>
                  <InfoRow
                    label="Name"
                    value={
                      <Link
                        component={RouterLink}
                        to={`/providers/${item.providerId}`}
                        underline="hover"
                      >
                        {item.providerName}
                      </Link>
                    }
                  />
                  <InfoRow label="Provider ID" value={item.providerId} />
                </>
              ) : (
                <EmptyState title="No provider assigned" description="This request is awaiting assignment." />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Service Details
              </Typography>
              <InfoRow label="Category" value={item.category} />
              <InfoRow label="Description" value={item.description} />
              <InfoRow
                label="Scheduled"
                value={`${formatDate(item.scheduledDate)} · ${item.scheduledTime}`}
              />
              <InfoRow label="Estimated Price" value={formatCurrency(item.estimatedPrice)} />
              <InfoRow label="Created" value={formatDate(item.createdAt)} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Location
              </Typography>
              <InfoRow label="Address" value={item.address} />
              <InfoRow label="City" value={item.city} />
              <Box
                sx={{
                  mt: 2,
                  height: 160,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'action.hover',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Map preview unavailable
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Job Status Timeline
              </Typography>
              {item.timeline.length === 0 ? (
                <EmptyState title="No timeline events" />
              ) : (
                <Box>
                  {item.timeline.map((event, index) => (
                    <Box key={event.id} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            mt: 0.5,
                          }}
                        />
                        {index < item.timeline.length - 1 && (
                          <Box sx={{ width: 2, flex: 1, backgroundColor: 'divider', my: 0.5 }} />
                        )}
                      </Box>
                      <Box sx={{ pb: 1 }}>
                        <Typography fontWeight={600} fontSize={14}>
                          {event.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(event.date)}
                        </Typography>
                        {event.note && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {event.note}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Payment Status
              </Typography>
              <StatusBadge status={item.paymentStatus} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="h3" sx={{ mb: 2 }}>
                Cancellation
              </Typography>
              {item.cancellationReason ? (
                <Typography variant="body2">{item.cancellationReason}</Typography>
              ) : (
                <EmptyState title="No cancellation" description="This request has not been cancelled." />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Complaints
              </Typography>
              {item.complaintId ? (
                <InfoRow
                  label="Complaint"
                  value={
                    <Link component={RouterLink} to={`/complaints/${item.complaintId}`} underline="hover">
                      {item.complaintId}
                    </Link>
                  }
                />
              ) : (
                <EmptyState title="No complaints" description="No complaints linked to this request." />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Reviews
              </Typography>
              {item.reviewId ? (
                <InfoRow
                  label="Review"
                  value={
                    <Link component={RouterLink} to={`/reviews/${item.reviewId}`} underline="hover">
                      {item.reviewId}
                    </Link>
                  }
                />
              ) : (
                <EmptyState title="No reviews" description="No reviews linked to this request." />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestDetailPage;
